import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import BottomNavBar from "../../components/BottomNavbar";
import Gnavbar from "../../components/Gnavbar";
import { getRequest, putRequest } from "../../helpers/api.helper";

const TermsAndConditionsEditor = () => {
  const [content, setContent] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState("");
  const quillRef = useRef<any>(null);

  // ✅ Detect language (basic check for Telugu or English)
  const detectLanguage = (text: string) => {
    if (!text) return "";
    const teluguPattern = /[\u0C00-\u0C7F]/;
    const englishPattern = /[a-zA-Z]/;
    if (teluguPattern.test(text)) return "Telugu";
    if (englishPattern.test(text)) return "English";
    return "Unknown";
  };

  // ✅ Auto resize editor height based on content
  const autoResizeEditor = () => {
    const editor = quillRef.current?.editor?.root;
    if (editor) {
      editor.style.height = "auto";
      editor.style.height = editor.scrollHeight + "px";
    }
  };

  // ✅ Fetch saved terms
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        setIsLoading(true);
        const response = await getRequest("/bureau/terms");
        if (response.success) {
          const termsContent = response.data || "";
          setContent(termsContent);
          setDetectedLanguage(detectLanguage(termsContent));
          setIsSaved(!!termsContent);
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error("Error fetching terms:", error);
        toast.error("Failed to fetch terms");
      } finally {
        setIsLoading(false);
      }
    };
    fetchTerms();
  }, []);

  // ✅ Save updated terms
  const handleSaveTerms = async () => {
    const trimmedContent = content.replace(/<p>\s*<\/p>/g, "").trim();
    try {
      setIsLoading(true);
      const response = await putRequest(`/bureau/terms`, {
        terms: trimmedContent,
      });
      if (response.success) {
        setIsSaved(true);
        setIsEditing(false);
        setDetectedLanguage(detectLanguage(trimmedContent));
        toast.success("Terms updated successfully!");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error saving terms:", error);
      toast.error("Failed to save terms");
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Clear editor
  const handleClearTerms = () => {
    setContent("");
    setIsSaved(false);
    toast.info("Terms cleared. Click save to confirm.");
  };

  // ✅ Adjust height dynamically whenever content changes
  useEffect(() => {
    if (isEditing) autoResizeEditor();
  }, [content, isEditing]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Gnavbar />

      <div className="container mx-auto px-4 py-6 flex-1">
        <div className="bg-white shadow-md rounded-lg p-4 md:p-6">
          <h1 className="text-2xl font-bold mb-4 text-center">
            Terms and Conditions
          </h1>

          {/* Language Indicator */}
          {detectedLanguage && (
            <div className="mb-4 p-2 rounded text-center bg-blue-100 text-blue-800">
              Detected Language: {detectedLanguage}
            </div>
          )}

          {/* Status Indicator */}
          {!isEditing && (
            <div
              className={`mb-4 p-2 rounded text-center ${
                isSaved
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {isSaved
                ? `Terms are saved for Bureau`
                : "No terms have been saved yet"}
            </div>
          )}

          {/* Loading Spinner */}
          {isLoading && (
            <div className="flex justify-center items-center mb-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}

          {/* Editor Area */}
          <div
            className={`border-0 md:border rounded ${
              isEditing ? "md:border-blue-500" : "md:border-gray-300"
            }`}
          >
            {isEditing ? (
              <div className="relative">
                <ReactQuill
                  ref={quillRef}
                  value={content}
                  onChange={(value) => {
                    setContent(value);
                    autoResizeEditor();
                  }}
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      [{ color: [] }, { background: [] }],
                      [{ align: [] }],
                      ["link", "image", "video"],
                      ["clean"],
                    ],
                  }}
                  formats={[
                    "header",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "list",
                    "bullet",
                    "indent",
                    "color",
                    "background",
                    "align",
                    "link",
                    "image",
                    "video",
                  ]}
                  theme="snow"
                  className={`h-[300px] md:h-[500px] pb-20 ${
                    detectedLanguage === "Telugu" ? "telugu" : ""
                  }`}
                  style={{
                    fontFamily:
                      detectedLanguage === "Telugu"
                        ? "Noto Sans Telugu, Arial, sans-serif"
                        : "Arial, sans-serif",
                    overflowY: "auto",
                  }}
                />
              </div>
            ) : (
              <div
                className={`p-4 max-h-[60vh] overflow-y-auto ${
                  detectedLanguage === "Telugu" ? "telugu-text" : ""
                }`}
                style={{
                  minHeight: "250px",
                  fontFamily:
                    detectedLanguage === "Telugu"
                      ? "Noto Sans Telugu, Arial, sans-serif"
                      : "Arial, sans-serif",
                }}
                dangerouslySetInnerHTML={{
                  __html: content || "<p>No terms and conditions</p>",
                }}
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col md:flex-row justify-end space-y-2 md:space-y-0 md:space-x-4 mt-6">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="w-full md:w-auto px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                  disabled={isLoading}
                >
                  Edit Terms
                </button>
                {content && (
                  <button
                    onClick={handleClearTerms}
                    className="w-full md:w-auto px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    Clear Terms
                  </button>
                )}
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="w-full md:w-auto px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTerms}
                  className="w-full md:w-auto px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                >
                  Save Changes
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <BottomNavBar />
    </div>
  );
};

export default TermsAndConditionsEditor;
